/**
 * Per-user saved list views (Operator Lists v2, spec section — saved views).
 * Per-user, connection-and-object-scoped prefsStore — same persistence
 * tier as sidebar pins and view prefs (see `useSidebarPins.ts`).
 */
import { useCallback, useEffect, useState } from "react";
import { prefsStore } from "@/app/preferences/prefsStore";
import type { ListViewKind } from "@/shared/listViews/viewCandidates";
import type { ColumnPrefs } from "./useColumnPrefs";

export interface SavedView {
  id: string;
  name: string;
  search: string;
  filters: Record<string, string>;
  view: ListViewKind;
  groupByFields: string[];
  aggregates: string[];
  /** Table sort. Optional so older stored views (pre-sort-capture) still load. */
  sortField?: string;
  sortDir?: "asc" | "desc";
  /** Kanban grouping axis, restored when view === "kanban". Optional for the same reason. */
  kanbanField?: string;
  /** Column show/hide/order/width layout. Optional so older stored views (pre-column-capture) still load. */
  columns?: ColumnPrefs;
}

const keyFor = (connectionId: string, objectName: string) =>
  `list_saved_views:${connectionId}:${objectName}`;

/** crypto.randomUUID exists in secure browser contexts but not in jsdom. */
function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isSavedView(v: unknown): v is SavedView {
  if (!v || typeof v !== "object") return false;
  const view = v as Partial<SavedView>;
  return (
    typeof view.id === "string" &&
    typeof view.name === "string" &&
    typeof view.search === "string" &&
    typeof view.filters === "object" &&
    view.filters !== null &&
    typeof view.view === "string" &&
    Array.isArray(view.groupByFields) &&
    Array.isArray(view.aggregates)
  );
}

function load(connectionId: string, objectName: string): SavedView[] {
  try {
    const raw = prefsStore.getItem(keyFor(connectionId, objectName));
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedView);
  } catch {
    return [];
  }
}

export function useSavedViews(connectionId: string, objectName: string) {
  const [views, setViews] = useState<SavedView[]>(() => load(connectionId, objectName));
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    setViews(load(connectionId, objectName));
    setActiveId(null);
  }, [connectionId, objectName]);

  const persist = useCallback(
    (next: SavedView[]) => {
      try {
        prefsStore.setItem(keyFor(connectionId, objectName), JSON.stringify(next));
      } catch {
        /* private browsing */
      }
    },
    [connectionId, objectName]
  );

  const save = useCallback(
    (name: string, snapshot: Omit<SavedView, "id" | "name">): SavedView => {
      const created: SavedView = { id: randomId(), name, ...snapshot };
      setViews((prev) => {
        const next = [...prev, created];
        persist(next);
        return next;
      });
      setActiveId(created.id);
      return created;
    },
    [persist]
  );

  const apply = useCallback(
    (id: string): SavedView | null => {
      const found = views.find((v) => v.id === id) ?? null;
      if (found) setActiveId(found.id);
      return found;
    },
    [views]
  );

  const remove = useCallback(
    (id: string) => {
      setViews((prev) => {
        const next = prev.filter((v) => v.id !== id);
        persist(next);
        return next;
      });
      setActiveId((prev) => (prev === id ? null : prev));
    },
    [persist]
  );

  const setActive = useCallback((id: string | null) => setActiveId(id), []);

  return { views, activeId, save, apply, remove, setActive };
}
